import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  ShieldCheck, UserPlus, Mail, KeyRound, Copy, Trash2,
  CheckCircle2, Ban, Plus, Clock, Users, Activity, Calendar as CalIcon,
} from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

const fmtDateOnly = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

export default function ReviewDashboard() {
  const [stats, setStats] = useState(null);
  const [allowlist, setAllowlist] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  // Add tester dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addNotes, setAddNotes] = useState("");
  const [adding, setAdding] = useState(false);

  // Create code dialog
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeLabel, setCodeLabel] = useState("");
  const [codeMaxUses, setCodeMaxUses] = useState(1);
  const [codeExpires, setCodeExpires] = useState(30);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      const [s, a, i] = await Promise.all([
        axios.get(`${API}/review/stats`, { withCredentials: true }),
        axios.get(`${API}/review/allowlist`, { withCredentials: true }),
        axios.get(`${API}/review/invites`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setAllowlist(a.data || []);
      setInvites(i.data || []);
      setLoading(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error("Couldn't load review dashboard");
      }
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const addTester = async () => {
    if (!addEmail.trim()) { toast.error("Email required"); return; }
    setAdding(true);
    try {
      await axios.post(
        `${API}/review/allowlist`,
        { email: addEmail.trim().toLowerCase(), notes: addNotes.trim() || null },
        { withCredentials: true },
      );
      toast.success(`Added ${addEmail.trim()}`);
      setAddOpen(false);
      setAddEmail(""); setAddNotes("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add tester");
    } finally { setAdding(false); }
  };

  const toggleStatus = async (entry) => {
    const next = entry.status === "approved" ? "revoked" : "approved";
    try {
      await axios.patch(
        `${API}/review/allowlist/${encodeURIComponent(entry.email)}`,
        { status: next },
        { withCredentials: true },
      );
      toast.success(next === "approved" ? "Re-approved" : "Access revoked");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update");
    }
  };

  const removeTester = async (email) => {
    if (!window.confirm(`Remove ${email} from the allowlist? They will lose access immediately.`)) return;
    try {
      await axios.delete(
        `${API}/review/allowlist/${encodeURIComponent(email)}`,
        { withCredentials: true },
      );
      toast.success("Removed");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove");
    }
  };

  const createCode = async () => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/review/invites`,
        { label: codeLabel.trim() || null, max_uses: codeMaxUses, expires_in_days: codeExpires },
        { withCredentials: true },
      );
      toast.success(`Code ${res.data.code} created`);
      setCodeOpen(false);
      setCodeLabel(""); setCodeMaxUses(1); setCodeExpires(30);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create code");
    } finally { setCreating(false); }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch (_) {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  const copyShareLink = async (code) => {
    const link = `${window.location.origin}/login?invite=${code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied");
    } catch (_) {
      toast.error("Couldn't copy");
    }
  };

  const revokeCode = async (code) => {
    if (!window.confirm(`Revoke code ${code}? Anyone holding it will no longer be able to redeem.`)) return;
    try {
      await axios.patch(`${API}/review/invites/${code}`, {}, { withCredentials: true });
      toast.success("Revoked");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't revoke");
    }
  };

  if (forbidden) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center space-y-3">
        <ShieldCheck className="w-12 h-12 mx-auto text-zinc-600" />
        <h1 className="text-2xl font-bold">Owner access required</h1>
        <p className="text-muted-foreground">
          The Review Dashboard is only available to listed Niko OS owners.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="review-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <ShieldCheck className="w-7 h-7 text-violet-400" />
            Review Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Closed-beta gate. Manage who gets in, generate invite codes, and watch activity.
          </p>
        </div>
      </div>

      {/* Stat cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={<Users className="w-5 h-5" />} label="Approved testers" value={stats.total_approved} />
          <StatCard icon={<Activity className="w-5 h-5" />} label="Active last 7d" value={stats.active_last_7_days} />
          <StatCard icon={<KeyRound className="w-5 h-5" />} label="Active invite codes" value={stats.active_invite_codes} />
          <StatCard icon={<Ban className="w-5 h-5" />} label="Revoked" value={stats.total_revoked} />
        </div>
      )}

      <Tabs defaultValue="testers" className="space-y-4">
        <TabsList>
          <TabsTrigger value="testers" data-testid="tab-testers">
            <Users className="w-4 h-4 mr-2" />Testers
          </TabsTrigger>
          <TabsTrigger value="invites" data-testid="tab-invites">
            <KeyRound className="w-4 h-4 mr-2" />Invite codes
          </TabsTrigger>
        </TabsList>

        {/* ============================ TESTERS ============================ */}
        <TabsContent value="testers" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => setAddOpen(true)}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="add-tester-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add tester by email
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              {allowlist.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground text-sm">
                  No testers yet — add the first one above.
                </div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {allowlist.map((row) => (
                    <div
                      key={row.email}
                      className="p-4 flex items-center gap-4 hover:bg-zinc-900/30"
                      data-testid={`tester-row-${row.email}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{row.email}</span>
                          {row.status === "approved" ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40">
                              <CheckCircle2 className="w-3 h-3 mr-1" />approved
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-red-400 border-red-500/40">
                              revoked
                            </Badge>
                          )}
                          {row.source === "owner_bootstrap" && (
                            <Badge variant="outline" className="text-violet-400 border-violet-500/40 text-[10px]">
                              owner
                            </Badge>
                          )}
                          {row.source === "invite_code" && (
                            <Badge variant="outline" className="text-zinc-400 border-zinc-700 text-[10px]">
                              via code
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                          {row.name && <span>{row.name}</span>}
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Last seen: {row.last_seen ? fmt(row.last_seen) : "never"}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <CalIcon className="w-3 h-3" />
                            Added: {fmtDateOnly(row.added_at)}
                          </span>
                          {row.notes && <span className="italic">"{row.notes}"</span>}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {row.source !== "owner_bootstrap" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleStatus(row)}
                              data-testid={`toggle-status-${row.email}`}
                            >
                              {row.status === "approved" ? (
                                <><Ban className="w-3.5 h-3.5 mr-1" />Revoke</>
                              ) : (
                                <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Re-approve</>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removeTester(row.email)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`remove-tester-${row.email}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* =========================== INVITE CODES ========================= */}
        <TabsContent value="invites" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => setCodeOpen(true)}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="create-invite-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Generate invite code
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              {invites.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground text-sm">
                  No invite codes yet. Generate one and share the link with a tester.
                </div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {invites.map((inv) => (
                    <div
                      key={inv.code}
                      className="p-4 flex items-center gap-4 hover:bg-zinc-900/30"
                      data-testid={`invite-row-${inv.code}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold tracking-widest text-violet-300">
                            {inv.code}
                          </span>
                          <Badge
                            className={
                              inv.status === "active"
                                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40"
                                : inv.status === "consumed"
                                ? "bg-zinc-500/20 text-zinc-300 border-zinc-500/40"
                                : "bg-red-500/20 text-red-400 border-red-500/40"
                            }
                          >
                            {inv.status}
                          </Badge>
                          {inv.label && (
                            <span className="text-sm text-muted-foreground">— {inv.label}</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-3">
                          <span>{inv.uses}/{inv.max_uses} uses</span>
                          <span>Expires {fmtDateOnly(inv.expires_at)}</span>
                          {inv.claimed_by_emails?.length > 0 && (
                            <span>Claimed by: {inv.claimed_by_emails.join(", ")}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyCode(inv.code)}
                          data-testid={`copy-code-${inv.code}`}
                        >
                          <Copy className="w-3.5 h-3.5 mr-1" />Code
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyShareLink(inv.code)}
                          data-testid={`copy-link-${inv.code}`}
                        >
                          <Mail className="w-3.5 h-3.5 mr-1" />Link
                        </Button>
                        {inv.status === "active" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => revokeCode(inv.code)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`revoke-code-${inv.code}`}
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== Add tester dialog ===== */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-tester-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-violet-400" />
              Add tester to allowlist
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="lulu@example.com"
                data-testid="add-tester-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={addNotes}
                onChange={(e) => setAddNotes(e.target.value)}
                placeholder="e.g. RN at St Mary's, met at NTI"
                data-testid="add-tester-notes"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddOpen(false)} disabled={adding}>
                Cancel
              </Button>
              <Button
                onClick={addTester}
                disabled={adding || !addEmail.trim()}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="confirm-add-tester-btn"
              >
                {adding ? "Adding…" : "Add tester"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Create code dialog ===== */}
      <Dialog open={codeOpen} onOpenChange={setCodeOpen}>
        <DialogContent data-testid="create-code-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-violet-400" />
              Generate invite code
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Label (who is this for?)</Label>
              <Input
                value={codeLabel}
                onChange={(e) => setCodeLabel(e.target.value)}
                placeholder="e.g. Lulu — RN friend"
                data-testid="create-code-label"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max uses</Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={codeMaxUses}
                  onChange={(e) => setCodeMaxUses(parseInt(e.target.value || "1", 10))}
                  data-testid="create-code-max-uses"
                />
              </div>
              <div className="space-y-2">
                <Label>Expires in (days)</Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={codeExpires}
                  onChange={(e) => setCodeExpires(parseInt(e.target.value || "30", 10))}
                  data-testid="create-code-expires"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Single-use is best for individuals. Multi-use is good for small teams (e.g. 5 RNs at same hospital).
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCodeOpen(false)} disabled={creating}>
                Cancel
              </Button>
              <Button
                onClick={createCode}
                disabled={creating}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="confirm-create-code-btn"
              >
                {creating ? "Creating…" : "Generate code"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-500/10 text-violet-400 flex items-center justify-center">
            {icon}
          </div>
          <div>
            <div className="text-2xl font-bold">{value ?? 0}</div>
            <div className="text-xs text-muted-foreground">{label}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
